import React, { useEffect, useState } from 'react';
import useDashboardContext from 'contexts/dashboard.context';
import FormStepper from 'components/base/form-stepper';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import { useNavigate } from 'react-router-dom';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import KitchenRequestForm from './kitchen-request-form';
import KitchenRequestPreview from './kitchen-request-preview';
import { AddKitchenRequestType, MixIngredientItem } from 'models/kitchen-request';
import { handleApiError } from 'utils/functions';

const initialProcurement = {
  ingredient_id: '',
  ingredient_name: '',
  quantity: '',
  item_type: 'prep'
};

const NewKitchenRequest = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const { setIsBackLink, setBackLinkTitle, activeStore } = useDashboardContext();
  const [formData, setFormData] = useState<AddKitchenRequestType[]>([initialProcurement]);
  const [isCreatingProcurementRequest, setIsCreatingProcurementRequest] = useState(false);

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to procurement');
    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);

  const setValue = (
    index: number,
    field: keyof AddKitchenRequestType,
    value: string | number | MixIngredientItem[]
  ) => {
    setFormData((prev) =>
      prev.map((data, i) => (i === index ? { ...data, [field]: value } : data))
    );
  };

  const addForm = () => setFormData([...formData, { ...initialProcurement }]);

  const removeItem = (index: number) => setFormData(formData.filter((data, i) => i !== index));

  function isFormFilled(data: AddKitchenRequestType[]): boolean {
    for (const item of data) {
      for (const key in item) {
        if (
          typeof item[key as keyof AddKitchenRequestType] === 'string' &&
          item[key as keyof AddKitchenRequestType] === ''
        ) {
          return false;
        }
      }
    }
    return true;
  }
  const data = {
    note: '.',
    store_id: activeStore?.id,
    item_type: 'prep',
    items: formData.map((request) => {
      return {
        mix_id: request.ingredient_id,
        quantity_requested: Number(request.quantity),
        item_type: request.item_type
      };
    })
  };

  const createProcurementRequest = async () => {
    if (!activeStore?.id) return;
    try {
      setIsCreatingProcurementRequest(true);
      const response = await apiService.post(`prep-kitchen-request`, {
        version: 'v2',
        data
      });
      if (response) {
        navigate(MAIN_STORE_ROUTE_KEYS.PREP_REQUESTS);
        toast(<ToastMessage message='Procurement request created successfully' type='success' />);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsCreatingProcurementRequest(false);
    }
  };

  const handleEdit = () => setStep(1);
  return (
    <div className='ml-2 md:ml-8'>
      <h4 className='text-[24px] font-semibold text-[#2E3031]'>Create Kitchen Request</h4>
      <p className='text-[#6F7376] text-sm mb-8'>
        Inventory running low on certain items? prepare your list
      </p>
      <div className=' w-full max-w-[678px]'>
        <FormStepper
          disabled={step === 1 ? !isFormFilled(formData) : false}
          step={step}
          title={step === 1 ? 'Make your selections' : 'Review selections'}
          subtitle={step === 2 ? 'Ensure you have all items needed selected' : ''}
          setStep={setStep}
          handleConfirmClick={() => {
            createProcurementRequest();
          }}
          loading={isCreatingProcurementRequest}
          handleCancelClick={() => {
            navigate(-1);
          }}>
          {step === 1 ? (
            <div>
              {formData.map((data, index) => (
                <KitchenRequestForm
                  key={index}
                  data={data}
                  index={index}
                  setValue={setValue}
                  removeItem={removeItem}
                />
              ))}
              <button onClick={addForm} className='text-[#0080E5] mt-2 font-medium text-sm'>
                + Add Item
              </button>
            </div>
          ) : (
            <div>
              <KitchenRequestPreview onEdit={handleEdit} formData={formData} />
            </div>
          )}
        </FormStepper>
      </div>
    </div>
  );
};

export default NewKitchenRequest;
